import api from './api';

export interface AutomaticPromotionCoupon {
  _id: string;
  code: string;
  description: string;
  promotionId: string;
  promotionName: string;
  schemeType: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
}

export interface AutomaticPromotionResult {
  hasPromotions: boolean;
  coupons: AutomaticPromotionCoupon[];
  message: string;
}

export interface AutomaticPromotionBulkResult {
  hasPromotions: boolean;
  allApplicableCoupons: AutomaticPromotionCoupon[];
  message: string;
}

/**
 * فحص الحملات التلقائية لمنتج معين وإرجاع الكوبونات المرتبطة بها
 * @param variantId معرف متغير المنتج
 * @param invoicePageId معرف صفحة الفاتورة
 * @param quantity الكمية (افتراضي 1)
 * @param originalPrice السعر الأصلي
 * @returns نتيجة فحص الحملات التلقائية مع الكوبونات
 */
export const checkAutomaticPromotions = async (
  variantId: string,
  invoicePageId: string,
  quantity: number = 1,
  originalPrice: number = 0
): Promise<AutomaticPromotionResult> => {
  try {
    console.log(`🔍 فحص الحملات التلقائية للمنتج: ${variantId}`);
    
    const response = await api.get(`/marketing/promotions/check-automatic/${variantId}/${invoicePageId}`, {
      params: {
        quantity,
        originalPrice
      }
    });

    const result = response.data as AutomaticPromotionResult;
    
    if (result.hasPromotions && result.coupons.length > 0) {
      console.log(`✅ تم العثور على ${result.coupons.length} كوبون تلقائي:`);
      result.coupons.forEach(coupon => {
        console.log(`  - ${coupon.code}: ${coupon.description || coupon.promotionName}`);
      });
    } else {
      console.log(`❌ لا توجد كوبونات تلقائية للمنتج`);
    }

    return result;
  } catch (error) {
    console.error('خطأ في فحص الحملات التلقائية:', error);
    
    // إرجاع نتيجة فارغة في حالة الخطأ لعدم تعطيل العملية
    return {
      hasPromotions: false,
      coupons: [],
      message: 'حدث خطأ أثناء فحص الحملات التلقائية'
    };
  }
};

/**
 * فحص الحملات التلقائية لعدة منتجات وإرجاع الكوبونات المرتبطة بها
 * @param items قائمة المنتجات مع معرفات المتغيرات والكميات
 * @param invoicePageId معرف صفحة الفاتورة
 * @returns نتائج فحص الحملات مع جميع الكوبونات القابلة للتطبيق
 */
export const checkAutomaticPromotionsForMultipleItems = async (
  items: Array<{
    variantId: string;
    quantity: number;
    originalPrice: number;
  }>,
  invoicePageId: string
): Promise<AutomaticPromotionBulkResult> => {
  try {
    console.log(`🔍 فحص الحملات التلقائية لـ ${items.length} منتج`);
    
    const response = await api.post('/marketing/promotions/check-automatic-bulk', {
      items,
      invoicePageId
    });

    const result = response.data as AutomaticPromotionBulkResult;
    
    if (result.hasPromotions && result.allApplicableCoupons.length > 0) {
      console.log(`✅ تم العثور على ${result.allApplicableCoupons.length} كوبون تلقائي إجمالي:`);
      result.allApplicableCoupons.forEach(coupon => {
        console.log(`  - ${coupon.code}: ${coupon.description || coupon.promotionName}`);
      });
    } else {
      console.log(`❌ لا توجد كوبونات تلقائية للمنتجات`);
    }

    return result;
  } catch (error) {
    console.error('خطأ في فحص الحملات التلقائية المجمع:', error);
    
    // إرجاع نتائج فارغة في حالة الخطأ
    return {
      hasPromotions: false,
      allApplicableCoupons: [],
      message: 'حدث خطأ أثناء فحص الحملات التلقائية'
    };
  }
};

/**
 * تطبيق الكوبونات التلقائية عبر مكون التسويق
 * @param coupons قائمة الكوبونات المراد تطبيقها
 * @param invoicePageId معرف صفحة الفاتورة
 * @param invoiceData بيانات الفاتورة الحالية
 * @returns نتيجة تطبيق الكوبونات
 */
export const applyAutomaticCoupons = async (
  coupons: AutomaticPromotionCoupon[],
  invoicePageId: string,
  invoiceData?: any
): Promise<{
  success: boolean;
  appliedCoupons: string[];
  failedCoupons: Array<{ code: string; reason: string }>;
  message: string;
}> => {
  try {
    console.log(`🎫 تطبيق ${coupons.length} كوبون تلقائي عبر مكون التسويق`);
    
    const appliedCoupons: string[] = [];
    const failedCoupons: Array<{ code: string; reason: string }> = [];
    
    // تطبيق كل كوبون بشكل منفصل عبر مكون التسويق
    for (const coupon of coupons) {
      try {
        console.log(`🔄 تطبيق الكوبون: ${coupon.code} (الحملة: ${coupon.promotionName})`);
        
        // استخدام خدمة التسويق لتطبيق الكوبون مع بيانات الفاتورة
        const marketingService = await import('./marketingService');
        
        let applicationResult;
        if (invoiceData) {
          // تطبيق الكوبون مع بيانات الفاتورة
          applicationResult = await marketingService.default.applyCouponToInvoice(
            coupon.code,
            invoicePageId,
            invoiceData
          );
        } else {
          // التحقق من صحة الكوبون فقط
          applicationResult = await marketingService.default.validateCoupon(
            coupon.code,
            invoicePageId
          );
        }
        
        if (applicationResult.success) {
          appliedCoupons.push(coupon.code);
          console.log(`✅ تم تطبيق الكوبون بنجاح: ${coupon.code}`);
        } else {
          failedCoupons.push({
            code: coupon.code,
            reason: applicationResult.message || 'فشل في تطبيق الكوبون'
          });
          console.log(`❌ فشل في تطبيق الكوبون: ${coupon.code} - ${applicationResult.message}`);
        }
      } catch (error) {
        console.error(`❌ خطأ في تطبيق الكوبون ${coupon.code}:`, error);
        failedCoupons.push({
          code: coupon.code,
          reason: 'حدث خطأ أثناء تطبيق الكوبون'
        });
      }
    }
    
    const successCount = appliedCoupons.length;
    const failureCount = failedCoupons.length;
    
    let message = '';
    if (successCount > 0 && failureCount === 0) {
      message = `تم تطبيق جميع الكوبونات التلقائية بنجاح (${successCount})`;
    } else if (successCount > 0 && failureCount > 0) {
      message = `تم تطبيق ${successCount} كوبون بنجاح، فشل في تطبيق ${failureCount} كوبون`;
    } else {
      message = `فشل في تطبيق جميع الكوبونات التلقائية (${failureCount})`;
    }
    
    return {
      success: successCount > 0,
      appliedCoupons,
      failedCoupons,
      message
    };
    
  } catch (error) {
    console.error('خطأ في تطبيق الكوبونات التلقائية:', error);
    return {
      success: false,
      appliedCoupons: [],
      failedCoupons: coupons.map(c => ({
        code: c.code,
        reason: 'حدث خطأ عام أثناء تطبيق الكوبونات'
      })),
      message: 'حدث خطأ أثناء تطبيق الكوبونات التلقائية'
    };
  }
};

/**
 * دالة مساعدة لفحص وتطبيق الحملات التلقائية عند إضافة منتج للفاتورة
 * @param variantId معرف متغير المنتج
 * @param invoicePageId معرف صفحة الفاتورة
 * @param quantity الكمية
 * @param originalPrice السعر الأصلي
 * @param invoiceData بيانات الفاتورة الحالية
 * @param autoApply هل يتم تطبيق الكوبونات تلقائياً (افتراضي: true)
 * @returns نتيجة فحص وتطبيق الحملات التلقائية
 */
export const checkAndApplyAutomaticPromotions = async (
  variantId: string,
  invoicePageId: string,
  quantity: number = 1,
  originalPrice: number = 0,
  invoiceData?: any,
  autoApply: boolean = true
): Promise<{
  promotionResult: AutomaticPromotionResult;
  applicationResult?: {
    success: boolean;
    appliedCoupons: string[];
    failedCoupons: Array<{ code: string; reason: string }>;
    message: string;
  };
}> => {
  try {
    console.log(`🔍 فحص وتطبيق الحملات التلقائية للمنتج: ${variantId}`);
    
    // أولاً: فحص الحملات التلقائية
    const promotionResult = await checkAutomaticPromotions(
      variantId,
      invoicePageId,
      quantity,
      originalPrice
    );
    
    // ثانياً: تطبيق الكوبونات إذا وجدت وكان التطبيق التلقائي مفعل
    let applicationResult;
    if (promotionResult.hasPromotions && promotionResult.coupons.length > 0 && autoApply) {
      console.log(`🎫 تطبيق ${promotionResult.coupons.length} كوبون تلقائياً مع بيانات الفاتورة`);
      applicationResult = await applyAutomaticCoupons(
        promotionResult.coupons,
        invoicePageId,
        invoiceData
      );
    }
    
    return {
      promotionResult,
      applicationResult
    };
    
  } catch (error) {
    console.error('خطأ في فحص وتطبيق الحملات التلقائية:', error);
    return {
      promotionResult: {
        hasPromotions: false,
        coupons: [],
        message: 'حدث خطأ أثناء فحص الحملات التلقائية'
      }
    };
  }
};

export default {
  checkAutomaticPromotions,
  checkAutomaticPromotionsForMultipleItems,
  applyAutomaticCoupons,
  checkAndApplyAutomaticPromotions
};